import { useState } from 'react';
import apiService from '../services/api';

/**
 * Getting a merchant to authorize an ACH debit.
 *
 * Two steps, in order, because Stripe needs both: an email on the profile —
 * which is also what creates the profile — and then a Checkout session for the
 * merchant to authorize in. Nothing here moves money; the debit is a separate
 * action on the invoice.
 *
 * Shared by the invoice page's collection panel and by the merchant list on
 * Merchant Invoices, so a merchant can be set up before their first sale as
 * well as after. One definition, so the two entry points cannot drift.
 *
 * The link is shown to be copied and sent by hand. Homecrowd emails nothing;
 * Stripe emails only the mandate confirmation, after the merchant authorizes.
 */

const btn = (disabled, bg) => ({
  padding: '9px 14px',
  borderRadius: '8px',
  border: 'none',
  background: disabled ? '#adb5bd' : bg,
  color: 'white',
  fontFamily: 'inherit',
  fontSize: '14px',
  fontWeight: 500,
  cursor: disabled ? 'not-allowed' : 'pointer',
  whiteSpace: 'nowrap',
});

const MerchantOnboarding = ({
  merchantKey,
  merchantName,
  profile,
  tone = { border: '#e5e7eb', text: '#4b5563' },
  onProfileSaved,
}) => {
  const [email, setEmail] = useState(profile?.billing_email || '');
  const [savingEmail, setSavingEmail] = useState(false);
  const [linking, setLinking] = useState(false);
  const [onboardUrl, setOnboardUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  const hasEmail = Boolean(profile?.billing_email);

  const saveEmail = async () => {
    setSavingEmail(true);
    setError(null);
    try {
      const res = await apiService.saveBillingProfile({
        merchant_key: merchantKey,
        display_name: merchantName,
        billing_email: email.trim(),
      });
      // This is the call that creates the profile, so the caller has to hear
      // about it — a merchant moves out of "not set up" on the strength of it.
      if (onProfileSaved) onProfileSaved(res?.profile);
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not save that email.');
    } finally {
      setSavingEmail(false);
    }
  };

  const makeLink = async () => {
    setLinking(true);
    setError(null);
    try {
      const res = await apiService.createBillingOnboardingLink({
        merchant_key: merchantKey,
      });
      setOnboardUrl(res?.url || '');
      if (onProfileSaved) onProfileSaved(res?.profile);
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not create the link.');
    } finally {
      setLinking(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard?.writeText(onboardUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          gap: '8px',
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Billing email at the merchant"
          style={{
            flex: '1 1 260px',
            padding: '9px 12px',
            border: '1px solid #dee2e6',
            borderRadius: '8px',
            fontFamily: 'inherit',
            fontSize: '14px',
          }}
        />
        <button
          type="button"
          onClick={saveEmail}
          disabled={savingEmail || !email.trim()}
          style={btn(savingEmail || !email.trim(), '#495057')}
        >
          {savingEmail ? 'Saving…' : 'Save email'}
        </button>
        <button
          type="button"
          onClick={makeLink}
          disabled={linking || !hasEmail}
          title={
            hasEmail
              ? ''
              : 'Save a billing email first — Stripe sends the mandate confirmation there.'
          }
          style={btn(linking || !hasEmail, '#0d6efd')}
        >
          {linking
            ? 'Creating…'
            : profile?.mandate_status === 'pending'
              ? 'Create another link'
              : 'Create onboarding link'}
        </button>
      </div>

      {error && (
        <div style={{ marginTop: '8px', color: '#b00020', fontSize: '13px' }}>
          {error}
        </div>
      )}

      {onboardUrl && (
        <div style={{ marginTop: '12px' }}>
          <div
            style={{ fontSize: '13px', color: tone.text, marginBottom: '6px' }}
          >
            Send this to {merchantName}. They authorize the debit on Stripe’s
            page — no money moves, and the link works once.
          </div>
          <div
            style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <code
              style={{
                flex: '1 1 320px',
                padding: '8px 10px',
                background: 'white',
                border: '1px solid #dee2e6',
                borderRadius: '6px',
                fontSize: '12px',
                wordBreak: 'break-all',
              }}
            >
              {onboardUrl}
            </code>
            <button type="button" onClick={copyLink} style={btn(false, '#495057')}>
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MerchantOnboarding;
